<script setup>
import { useI18n } from 'vue-i18n'
import { KkModal, KkButton, KkStatusLabel, KkLoading } from '../../../base'
import { basicDateFormat } from '../../../../modules/date'

const { t } = useI18n()

defineProps({
  isVisible: {
    type: Boolean,
    required: true,
  },
  loading: {
    type: Boolean,
    required: true,
  },
  brands: {
    type: Array,
    required: true,
  }
})

const emit = defineEmits(['close'])
const statusNumber = (status) => {
  switch(status) {
  case 'published':
    return 1
  case 'unpublished':
    return 2
  case 'closed':
    return 3
  default:
    return ''
  }
}
const isClosed = (status) => {
  return ['closed'].includes(status)
}
const isDisabled = (status) => {
  return ['closed', 'unpublished'].includes(status)
}

</script>

<template>
  <KkModal width="1076px" :is-visible="isVisible" @change="emit('close')">
    <div class="content__header">
      <template v-if="!loading">
        <span class="targetBrandCount">{{ $t('labels.targetBrandCount') }}</span>
        <span> {{ brands.length }}{{ $t('labels.brandCountUnit') }} </span>
      </template>
    </div>
    <div class="content__body">
      <KkLoading v-if="loading" />
      <div v-else class="p-table">
        <div class="p-table__content">
          <table>
            <thead>
              <tr class="p-table__content__top">
                <th>{{ $t('labels.brand') }}</th>
                <th>{{ $t('labels.status') }}</th>
                <th>{{ $t('labels.date.start') }}</th>
                <th>{{ $t('labels.date.end') }}</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="brand in brands" :key="brand.name">
                <tr v-for="(history, index) in brand.histories"
                    :key="`history__${index}`"
                    :class="{ 'disable': isClosed(brand.status) }"
                >
                  <template v-if="index === 0">
                    <td :rowspan="brand.histories.length">
                      <div class="flexContainer">
                        <div class="icon" :class="{ 'icon--disable': isDisabled(brand.status) }"><img :src="brand.imageUrl" alt=""></div>
                        <div class="name"><p>{{ brand.name }}</p></div>
                      </div>
                    </td>
                    <td :rowspan="brand.histories.length">
                      <KkStatusLabel
                        :status="statusNumber(brand.status)"
                        :text="$t('models.surveyBrand.status.' + brand.status)"
                        :sync-text-color="statusNumber(brand.status) === 2"
                      />
                    </td>
                  </template>
                  <td>{{ basicDateFormat(history.publishedAt) || "-" }}</td>
                  <td>{{ basicDateFormat(history.closedAt) || "-" }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div class="content__footer">
      <div class="buttonGroup">
        <div class="buttonGroup__item">
          <KkButton
            color="secondary"
            outlined
            :text="t('labels.close')"
            @click="emit('close')"
          />
        </div>
      </div>
    </div>
  </KkModal>
</template>

<style lang="scss" scoped>
.item {
  padding: 0 12px 12px;
}

.content {
  &__header {
    padding: 24px 32px 16px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    border-bottom: 1px solid #e4e7eb;
    font-weight: 700;
    font-size: 20px;
    line-height: 1.5;
  }

  &__body {
    padding: 24px;
    height: 50vh;
  }

  &__item {
    padding: 0 12px 12px;
  }

  &__footer {
    padding: 32px;
    border-top: 1px solid #e4e7eb;
  }
}

.buttonGroup {
  display: flex;
  flex-grow: 0;
  justify-content: flex-end;
}

.flexContainer {
  display: flex;
}

.icon {
  width: 75px;
  height: 75px;
  padding: 4px;
  margin-right: 12px;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  &--disable {
    opacity: 0.4;
    filter: grayscale(100%);
  }
}

.name {
  margin: auto 0;
}

.disable {
  background-color: #f8f9fa;
  td {
    background-color: transparent;
  }
}
.p-table {
  margin-top: 0;
  height: 100%;
}
.p-table__content {
  height: 100%;
  table {
    overflow-y: auto;
  }
  th {
    top: 0;
    position: sticky;
    // NOTE: スタッキングコンテキスト対応 filter, opacityをtd内で利用しているため追加
    z-index: 1;
  }
}
.targetBrandCount {
  font-weight: normal;
}
</style>
